/**
 * PropertyRequestsPanel.js
 *
 * Lists user's property-finding requests.
 * Detail view: each section has an inline ✏️ edit form — saves directly to API.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiRoutes } from '../../../../api';
import {
    listRequests, deleteRequest,
    updateDraft, upsertAmenities, upsertGeoFeatures, getMatches,
} from '../../../../api/services/propertyRequestService';
import GEO_FEATURE_MAPPING from '../../../PropertyRequest/geoFeatureMapping';
import AmenityRanker from '../../../PropertyRequest/components/AmenityRanker';

// ── constants ─────────────────────────────────────────────────────────────────

const INTENT_LABELS = { BUY: 'خرید', RENT: 'اجاره', PRESALE: 'پیش‌خرید', COLAB: 'مشارکت' };
const PET_LABELS    = { cat: 'گربه 🐱', dog: 'سگ 🐶', bird: 'پرنده 🐦' };
const DEED_TYPES    = [{ value: 'NONE', label: 'فرقی ندارد' }, { value: 'SINGLE', label: 'سند تک‌برگ' }];
const CONVERSION    = 33;
const STATUS_META   = {
    DRAFT:     { label: 'پیش‌نویس',    color: '#78909c', bg: '#f0f3ff' },
    SUBMITTED: { label: 'ثبت‌شده',     color: '#1565c0', bg: '#e3f2fd' },
    ACTIVE:    { label: 'فعال',        color: '#2e7d32', bg: '#e8f5e9' },
    MATCHED:   { label: 'نتایج آماده', color: '#6a1b9a', bg: '#f3e5f5' },
    CLOSED:    { label: 'بسته‌شده',    color: '#78909c', bg: '#eceff1' },
};

const fmt     = (v) => v ? Number(v).toLocaleString('fa-IR') : null;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fa-IR') : '—';

function geoLabel(item) {
    const m = GEO_FEATURE_MAPPING[item.geo_feature];
    if (!m) return `ویژگی #${item.geo_feature}`;
    const opt = m.options.find(o => o.value === item.value);
    return m.question.replace('؟', '') + (opt ? ` — ${opt.label}` : '');
}

// ── shared UI ─────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
    const s = STATUS_META[status] || { label: status, color: '#78909c', bg: '#f5f5f5' };
    return (
        <span style={{
            padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700,
            color: s.color, background: s.bg, border: `1px solid ${s.color}44`, whiteSpace: 'nowrap',
        }}>{s.label}</span>
    );
}

function DetailRow({ label, value }) {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="prq-detail-row">
            <span className="prq-detail-label">{label}</span>
            <span className="prq-detail-value">{value}</span>
        </div>
    );
}

// SectionCard with optional inline edit
function SectionCard({ title, icon, defaultOpen = true, canEdit = false, onEditToggle, editing, children, editForm }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="prq-section-card">
            <div className={`prq-section-header ${open ? 'prq-section-open' : ''}`}>
                <button
                    type="button"
                    className="prq-section-toggle"
                    onClick={() => setOpen(o => !o)}
                >
                    <span>{icon}</span>
                    <span className="prq-section-title">{title}</span>
                    <span className="prq-chevron">{open ? '▲' : '▼'}</span>
                </button>
                {canEdit && (
                    <button
                        type="button"
                        className={`prq-edit-btn ${editing ? 'prq-edit-active' : ''}`}
                        onClick={() => { setOpen(true); onEditToggle(); }}
                    >
                        {editing ? '✕ لغو' : '✏️ ویرایش'}
                    </button>
                )}
            </div>
            {open && (
                <div className="prq-section-body">
                    {editing && editForm ? editForm : children}
                </div>
            )}
        </div>
    );
}

// ── edit forms ────────────────────────────────────────────────────────────────

function BudgetEditForm({ req, onSave, onCancel }) {
    const isRent = req.intent === 'RENT';
    const [purchaseMax,  setPurchaseMax]  = useState(req.purchase_budget_max || '');
    const [depositMax,   setDepositMax]   = useState(req.deposit_budget_max  || '');
    const [rentMax,      setRentMax]      = useState(req.rent_budget_max     || '');
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    const save = async () => {
        setSaving(true); setError('');
        try {
            const patch = isRent
                ? { deposit_budget_max: depositMax ? Number(depositMax) : null, rent_budget_max: rentMax ? Number(rentMax) : null }
                : { purchase_budget_max: purchaseMax ? Number(purchaseMax) : null };
            await updateDraft(req.id, patch);
            onSave(patch);
        } catch { setError('خطا در ذخیره.'); }
        finally { setSaving(false); }
    };

    return (
        <div className="prq-edit-form">
            {!isRent ? (
                <div className="prw-field">
                    <label className="prw-label">حداکثر بودجه خرید (تومان)</label>
                    <input type="number" className="prw-input" value={purchaseMax}
                        onChange={e => setPurchaseMax(e.target.value)} placeholder="تومان" />
                </div>
            ) : (
                <div className="s3-row">
                    <div className="prw-field">
                        <label className="prw-label">سقف ودیعه (تومان)</label>
                        <input type="number" className="prw-input" value={depositMax}
                            onChange={e => setDepositMax(e.target.value)} placeholder="تومان" />
                    </div>
                    <div className="prw-field">
                        <label className="prw-label">سقف اجاره (تومان/ماه)</label>
                        <input type="number" className="prw-input" value={rentMax}
                            onChange={e => setRentMax(e.target.value)} placeholder="تومان" />
                    </div>
                </div>
            )}
            {error && <p className="prq-inline-error">{error}</p>}
            <div className="prq-edit-actions">
                <button className="prq-btn prq-btn-save" onClick={save} disabled={saving}>
                    {saving ? '...' : 'ذخیره'}
                </button>
                <button className="prq-btn prq-btn-cancel" onClick={onCancel}>انصراف</button>
            </div>
        </div>
    );
}

function SpecsEditForm({ req, onSave, onCancel }) {
    const [minArea,  setMinArea]  = useState(req.min_area_sqm      || '');
    const [maxArea,  setMaxArea]  = useState(req.max_area_sqm      || '');
    const [maxAge,   setMaxAge]   = useState(req.max_property_age  || '');
    const [deedType, setDeedType] = useState(req.deed_type         || 'NONE');
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    const save = async () => {
        if (minArea && maxArea && Number(minArea) > Number(maxArea)) {
            setError('حداقل متراژ نمی‌تواند بیشتر از حداکثر باشد.'); return;
        }
        setSaving(true); setError('');
        try {
            const patch = {};
            if (minArea)  patch.min_area_sqm     = Number(minArea);
            if (maxArea)  patch.max_area_sqm     = Number(maxArea);
            if (maxAge)   patch.max_property_age = Number(maxAge);
            patch.deed_type = deedType;
            await updateDraft(req.id, patch);
            onSave(patch);
        } catch { setError('خطا در ذخیره.'); }
        finally { setSaving(false); }
    };

    return (
        <div className="prq-edit-form">
            <div className="s3-row">
                <div className="prw-field">
                    <label className="prw-label">حداقل متراژ (م²)</label>
                    <input type="number" className="prw-input" value={minArea}
                        onChange={e => setMinArea(e.target.value)} placeholder="مثال: ۶۰" />
                </div>
                <div className="prw-field">
                    <label className="prw-label">حداکثر متراژ (م²)</label>
                    <input type="number" className="prw-input" value={maxArea}
                        onChange={e => setMaxArea(e.target.value)} placeholder="مثال: ۱۵۰" />
                </div>
            </div>
            <div className="s3-row">
                <div className="prw-field">
                    <label className="prw-label">حداکثر سن بنا (سال)</label>
                    <input type="number" className="prw-input" value={maxAge}
                        onChange={e => setMaxAge(e.target.value)} placeholder="مثال: ۱۰" />
                </div>
                <div className="prw-field">
                    <label className="prw-label">نوع سند</label>
                    <select className="prw-select" value={deedType} onChange={e => setDeedType(e.target.value)}>
                        {DEED_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                </div>
            </div>
            {error && <p className="prq-inline-error">{error}</p>}
            <div className="prq-edit-actions">
                <button className="prq-btn prq-btn-save" onClick={save} disabled={saving}>
                    {saving ? '...' : 'ذخیره'}
                </button>
                <button className="prq-btn prq-btn-cancel" onClick={onCancel}>انصراف</button>
            </div>
        </div>
    );
}

function AmenitiesEditForm({ req, onSave, onCancel }) {
    const [allAmenities, setAllAmenities] = useState([]);
    const [selected, setSelected] = useState(
        (req.amenities_data || []).map(a => ({
            amenity: a.amenity, amenity_label: a.amenity_label,
            value_type: a.amenity_value_type, rank: a.rank,
            required: a.required, value: a.value,
        }))
    );
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [error,   setError]   = useState('');

    useEffect(() => {
        api.get(apiRoutes.properties.amenities)
            .then(r => setAllAmenities(r.data?.results ?? r.data))
            .catch(() => setError('خطا در بارگذاری'))
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true); setError('');
        try {
            const items = selected.map(s => ({
                amenity: s.amenity, rank: s.rank, required: s.required, value: s.value ?? null,
            }));
            await upsertAmenities(req.id, items);
            onSave({ amenities_data: selected });
        } catch { setError('خطا در ذخیره.'); }
        finally { setSaving(false); }
    };

    if (loading) return <p className="prq-loading">در حال بارگذاری...</p>;

    return (
        <div className="prq-edit-form">
            <AmenityRanker allAmenities={allAmenities} selected={selected} onChange={setSelected} />
            {error && <p className="prq-inline-error">{error}</p>}
            <div className="prq-edit-actions">
                <button className="prq-btn prq-btn-save" onClick={save} disabled={saving}>
                    {saving ? '...' : 'ذخیره'}
                </button>
                <button className="prq-btn prq-btn-cancel" onClick={onCancel}>انصراف</button>
            </div>
        </div>
    );
}

function GeoFeaturesEditForm({ req, onSave, onCancel }) {
    const [allFeatures, setAllFeatures] = useState([]);
    const [answers,  setAnswers]  = useState(() => {
        const m = {};
        (req.geo_features_data || []).forEach(item => { m[item.geo_feature] = item.value; });
        return m;
    });
    const [ranked, setRanked] = useState(
        (req.geo_features_data || []).map((item, i) => ({
            geo_feature: item.geo_feature, rank: i + 1,
            required: item.required, value: item.value,
        }))
    );
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [error,   setError]   = useState('');

    useEffect(() => {
        api.get(apiRoutes.geo.features, { params: { for_matching: true } })
            .then(r => setAllFeatures(r.data?.results ?? r.data))
            .catch(() => setError('خطا در بارگذاری'))
            .finally(() => setLoading(false));
    }, []);

    const handleAnswer = (featureId, value) => {
        setAnswers(prev => {
            const next = { ...prev, [featureId]: value };
            setRanked(prevRanked => {
                const stillSelected = Object.entries(next)
                    .filter(([, v]) => v !== null && v !== undefined)
                    .map(([id]) => Number(id));
                const kept  = prevRanked.filter(r => stillSelected.includes(r.geo_feature));
                const added = stillSelected
                    .filter(id => !kept.find(r => r.geo_feature === id))
                    .map(id => ({ geo_feature: id, rank: 0, required: false, value: next[id] }));
                return [...kept, ...added].map((item, i) => ({
                    ...item, value: next[item.geo_feature], rank: i + 1,
                }));
            });
            return next;
        });
    };

    const moveItem = (idx, dir) => {
        setRanked(prev => {
            const next = [...prev];
            const target = idx + dir;
            if (target < 0 || target >= next.length) return prev;
            [next[idx], next[target]] = [next[target], next[idx]];
            return next.map((item, i) => ({ ...item, rank: i + 1 }));
        });
    };

    const toggleRequired = (featureId) => {
        setRanked(prev => prev.map(item =>
            item.geo_feature === featureId ? { ...item, required: !item.required } : item
        ));
    };

    const removeFeature = (featureId) => {
        setAnswers(prev => { const n = { ...prev }; delete n[featureId]; return n; });
        setRanked(prev => prev.filter(r => r.geo_feature !== featureId).map((item, i) => ({ ...item, rank: i + 1 })));
    };

    const save = async () => {
        setSaving(true); setError('');
        try {
            await upsertGeoFeatures(req.id, ranked.map(r => ({
                geo_feature: r.geo_feature, rank: r.rank, required: r.required, value: r.value ?? null,
            })));
            onSave({ geo_features_data: ranked });
        } catch { setError('خطا در ذخیره.'); }
        finally { setSaving(false); }
    };

    if (loading) return <p className="prq-loading">در حال بارگذاری...</p>;

    const mappedFeatures = allFeatures.filter(f => GEO_FEATURE_MAPPING[f.id]);
    const featureName = (id) => allFeatures.find(f => f.id === id)?.description || '';

    return (
        <div className="prq-edit-form">
            <div className="s5g-questions">
                {mappedFeatures.map(feature => {
                    const mapping = GEO_FEATURE_MAPPING[feature.id];
                    const current = answers[feature.id];
                    const answered = feature.id in answers;
                    return (
                        <div key={feature.id} className={`s5g-question-card ${answered ? 's5g-question-answered' : ''}`}>
                            <p className="s5g-question-text">{mapping.question}</p>
                            <div className="s5g-options">
                                {mapping.options.map((opt, i) => {
                                    const isSel = answered && current === opt.value;
                                    return (
                                        <button key={i} type="button"
                                            className={`s5g-option-btn ${isSel ? 's5g-option-selected' : ''}`}
                                            onClick={() => handleAnswer(feature.id, opt.value)}
                                        >
                                            {isSel && <span className="s5g-option-check">✓</span>}
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
            {ranked.length > 0 && (
                <div className="s5g-ranker-section">
                    <p className="s5g-ranker-title">اولویت‌بندی</p>
                    <ul className="ar-rank-list">
                        {ranked.map((item, idx) => (
                            <li key={item.geo_feature} className="ar-rank-item">
                                <span className="ar-rank-num">{idx + 1}</span>
                                <div className="s5g-move-btns">
                                    <button type="button" className="s5g-move-btn" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>▲</button>
                                    <button type="button" className="s5g-move-btn" onClick={() => moveItem(idx, 1)} disabled={idx === ranked.length - 1}>▼</button>
                                </div>
                                <span className="ar-rank-label">{featureName(item.geo_feature)}</span>
                                <button type="button" className={`ar-req-btn ${item.required ? 'ar-req-on' : ''}`} onClick={() => toggleRequired(item.geo_feature)}>
                                    {item.required ? 'الزامی ✓' : 'الزامی'}
                                </button>
                                <button type="button" className="ar-remove-btn" onClick={() => removeFeature(item.geo_feature)} aria-label="حذف">✕</button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {error && <p className="prq-inline-error">{error}</p>}
            <div className="prq-edit-actions">
                <button className="prq-btn prq-btn-save" onClick={save} disabled={saving}>{saving ? '...' : 'ذخیره'}</button>
                <button className="prq-btn prq-btn-cancel" onClick={onCancel}>انصراف</button>
            </div>
        </div>
    );
}

function HouseholdEditForm({ req, onSave, onCancel }) {
    const [adults,   setAdults]   = useState(req.adults_count   ?? 1);
    const [children, setChildren] = useState(req.children_count ?? 0);
    const [pet,      setPet]      = useState(req.pet            || '');
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    const save = async () => {
        setSaving(true); setError('');
        try {
            const patch = { adults_count: adults, children_count: children, pet: pet || null };
            await updateDraft(req.id, patch);
            onSave(patch);
        } catch { setError('خطا در ذخیره.'); }
        finally { setSaving(false); }
    };

    const Counter = ({ label, value, onChange, min = 0 }) => (
        <div className="s7-counter">
            <span className="s7-counter-label">{label}</span>
            <div className="s7-counter-ctrl">
                <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>−</button>
                <span className="s7-counter-val">{value}</span>
                <button type="button" onClick={() => onChange(value + 1)}>+</button>
            </div>
        </div>
    );

    return (
        <div className="prq-edit-form">
            <div className="s7-counters">
                <Counter label="بزرگسال" value={adults}   onChange={setAdults}   min={1} />
                <Counter label="کودک"    value={children} onChange={setChildren} />
            </div>
            <div className="prw-field" style={{ marginTop: 16 }}>
                <label className="prw-label">حیوان خانگی</label>
                <div className="prw-option-grid s7-pet-grid">
                    {[{ value: '', label: 'بدون حیوان' }, { value: 'cat', label: 'گربه 🐱' }, { value: 'dog', label: 'سگ 🐶' }, { value: 'bird', label: 'پرنده 🐦' }].map(p => (
                        <button key={p.value} type="button"
                            className={`prw-option-card ${pet === p.value ? 'selected' : ''}`}
                            onClick={() => setPet(p.value)}
                        >{p.label}</button>
                    ))}
                </div>
            </div>
            {error && <p className="prq-inline-error">{error}</p>}
            <div className="prq-edit-actions">
                <button className="prq-btn prq-btn-save" onClick={save} disabled={saving}>{saving ? '...' : 'ذخیره'}</button>
                <button className="prq-btn prq-btn-cancel" onClick={onCancel}>انصراف</button>
            </div>
        </div>
    );
}

// ── match results ─────────────────────────────────────────────────────────────

function MatchCard({ listingId, score }) {
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(apiRoutes.listings.publicDetail(listingId))
            .then(r => setListing(r.data))
            .catch(() => setListing(null))
            .finally(() => setLoading(false));
    }, [listingId]);

    if (loading) return <div className="prq-match-card prq-match-card-loading" />;
    if (!listing) return null;

    const prop    = listing.property || {};
    const isRent  = listing.listing_type === 'RENT';
    const price   = parseFloat(listing.price);
    const deposit = parseFloat(listing.deposit);
    // score from OpenSearch is a raw float — cap display at 99% for raw scores > 1
    const scorePct = score <= 1 ? Math.round(score * 100) : Math.min(99, Math.round(score));

    return (
        <div className="prq-match-card" onClick={() => window.open(`/listing/${listingId}`, '_blank')}>
            {/* Match score badge */}
            <div className="prq-match-score-badge">
                <span className="prq-match-score-val">{scorePct}%</span>
                <span className="prq-match-score-label">تطابق</span>
            </div>

            {/* Thumbnail */}
            <div className="prq-match-img">
                <img
                    src={listing.primary_image?.url || '/default-property-image.jpg'}
                    alt={prop.name || 'تصویر ملک'}
                    loading="lazy"
                />
            </div>

            {/* Info */}
            <div className="prq-match-body">
                <span className={`prq-match-type-chip ${isRent ? 'rent' : 'sale'}`}>
                    {isRent ? 'رهن و اجاره' : 'فروش'}
                </span>
                <p className="prq-match-name">{prop.name || 'بدون نام'}</p>

                {isRent ? (
                    <>
                        {price   > 0 && <p className="prq-match-price-primary">{price.toLocaleString()} {listing.currency}/ماه</p>}
                        {deposit > 0 && <p className="prq-match-price-secondary">ودیعه: {deposit.toLocaleString()} {listing.currency}</p>}
                    </>
                ) : (
                    price > 0 && <p className="prq-match-price-primary">{price.toLocaleString()} {listing.currency}</p>
                )}

                <div className="prq-match-meta">
                    {prop.area        && <span>📐 {prop.area} م²</span>}
                    {prop.neighborhood && <span>📍 {prop.neighborhood}</span>}
                </div>
            </div>
        </div>
    );
}

function MatchResults({ requestId }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState('');
    const [page, setPage]       = useState(1);
    const PAGE_SIZE = 10;

    const load = useCallback(async (p) => {
        setLoading(true); setError('');
        try {
            const res = await getMatches(requestId, p, PAGE_SIZE);
            setData(res);
        } catch {
            setError('خطا در بارگذاری نتایج.');
        } finally {
            setLoading(false);
        }
    }, [requestId]);

    useEffect(() => { load(page); }, [load, page]);

    if (loading) return <div className="prq-matches-loading"><p>در حال جستجوی ملک‌های منطبق...</p></div>;

    if (error) return (
        <div className="prq-matches-error">
            <p>{error}</p>
            <button className="prq-btn prq-btn-save" onClick={() => load(page)}>تلاش مجدد</button>
        </div>
    );

    if (!data?.results?.length) return (
        <div className="prq-matches-empty">
            <span style={{ fontSize: 36 }}>🔍</span>
            <p>ملکی منطبق با شرایط شما یافت نشد.</p>
        </div>
    );

    return (
        <div className="prq-matches">
            <p className="prq-matches-count">{data.total} ملک منطبق یافت شد</p>
            <div className="prq-matches-grid">
                {data.results.map(item => (
                    <MatchCard key={item.listing_id} listingId={item.listing_id} score={item.score} />
                ))}
            </div>
            {data.total > PAGE_SIZE && (
                <div className="prq-matches-pagination">
                    <button className="prq-btn prq-btn-cancel" disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}>قبلی</button>
                    <span>صفحه {page}</span>
                    <button className="prq-btn prq-btn-cancel" disabled={data.results.length < PAGE_SIZE}
                        onClick={() => setPage(p => p + 1)}>بعدی</button>
                </div>
            )}
        </div>
    );
}

// ── detail view ───────────────────────────────────────────────────────────────

function RequestDetail({ req: initialReq, onBack }) {
    const [req, setReq] = useState(initialReq);
    const [editing, setEditing] = useState(null); // 'budget' | 'specs' | 'amenities' | 'geo' | 'household'

    const patch = (section, data) => {
        setReq(prev => ({ ...prev, ...data }));
        setEditing(null);
    };

    const canEdit = req.status === 'DRAFT' || req.status === 'SUBMITTED';

    return (
        <div className="prq-detail">
            <div className="prq-detail-topbar">
                <button className="prq-back-btn" onClick={onBack}>← بازگشت</button>
            </div>

            <div className="prq-detail-header">
                <div>
                    <h3 className="prq-detail-title">{INTENT_LABELS[req.intent] || req.intent || 'درخواست'} ملک</h3>
                    <p className="prq-detail-date">📅 تاریخ ثبت: {fmtDate(req.created_at)}</p>
                </div>
                <StatusBadge status={req.status} />
            </div>

            <div className="prq-sections">

                <SectionCard title="هدف و نوع ملک" icon="🏠" defaultOpen>
                    <DetailRow label="نوع معامله" value={INTENT_LABELS[req.intent] || req.intent} />
                    <DetailRow label="نوع ملک"    value={req.property_type_name} />
                    <DetailRow label="کاربری"     value={req.usage_name} />
                    <DetailRow label="نوع سند"    value={req.deed_type_display} />
                </SectionCard>

                <SectionCard title="بودجه" icon="" defaultOpen
                    canEdit={canEdit} editing={editing === 'budget'}
                    onEditToggle={() => setEditing(e => e === 'budget' ? null : 'budget')}
                    editForm={<BudgetEditForm req={req} onSave={d => patch('budget', d)} onCancel={() => setEditing(null)} />}
                >
                    {req.intent === 'RENT' ? (
                        <>
                            <DetailRow label="سقف ودیعه" value={fmt(req.deposit_budget_max) ? `${fmt(req.deposit_budget_max)} تومان`  : null} />
                            <DetailRow label="سقف اجاره" value={fmt(req.rent_budget_max)    ? `${fmt(req.rent_budget_max)} تومان/ماه` : null} />
                            <DetailRow label="بودجه کل"  value={fmt(req.total_budget)       ? `${fmt(req.total_budget)} تومان`        : null} />
                        </>
                    ) : (
                        <DetailRow label="حداکثر بودجه خرید" value={fmt(req.purchase_budget_max) ? `${fmt(req.purchase_budget_max)} تومان` : null} />
                    )}
                </SectionCard>

                <SectionCard title="مشخصات ملک" icon="📐" defaultOpen
                    canEdit={canEdit} editing={editing === 'specs'}
                    onEditToggle={() => setEditing(e => e === 'specs' ? null : 'specs')}
                    editForm={<SpecsEditForm req={req} onSave={d => patch('specs', d)} onCancel={() => setEditing(null)} />}
                >
                    <DetailRow label="متراژ" value={(req.min_area_sqm || req.max_area_sqm) ? `${req.min_area_sqm || '—'} تا ${req.max_area_sqm || '—'} م²` : null} />
                    <DetailRow label="حداکثر سن بنا" value={req.max_property_age ? `${req.max_property_age} سال` : null} />
                    <DetailRow label="نوع سند" value={req.deed_type_display} />
                </SectionCard>

                <SectionCard title="امکانات" icon="✨" defaultOpen={false}
                    canEdit={canEdit} editing={editing === 'amenities'}
                    onEditToggle={() => setEditing(e => e === 'amenities' ? null : 'amenities')}
                    editForm={<AmenitiesEditForm req={req} onSave={d => patch('amenities', d)} onCancel={() => setEditing(null)} />}
                >
                    {req.amenities_data?.length ? (
                        <ol className="prq-ranked-list">
                            {req.amenities_data.map((a, i) => (
                                <li key={i} className="prq-ranked-item">
                                    <span className="prq-rank-num">{i + 1}</span>
                                    <span>{a.amenity_label || `#${a.amenity}`}</span>
                                    {a.required && <span className="prq-req-badge">الزامی</span>}
                                </li>
                            ))}
                        </ol>
                    ) : <span className="prq-empty-inline">انتخاب نشده</span>}
                </SectionCard>

                <SectionCard title="ترجیحات محیطی" icon="🌳" defaultOpen={false}
                    canEdit={canEdit} editing={editing === 'geo'}
                    onEditToggle={() => setEditing(e => e === 'geo' ? null : 'geo')}
                    editForm={<GeoFeaturesEditForm req={req} onSave={d => patch('geo', d)} onCancel={() => setEditing(null)} />}
                >
                    {req.geo_features_data?.length ? (
                        <ol className="prq-ranked-list">
                            {req.geo_features_data.map((item, i) => (
                                <li key={i} className="prq-ranked-item">
                                    <span className="prq-rank-num">{i + 1}</span>
                                    <span>{geoLabel(item)}</span>
                                    {item.required && <span className="prq-req-badge">الزامی</span>}
                                </li>
                            ))}
                        </ol>
                    ) : <span className="prq-empty-inline">انتخاب نشده</span>}
                </SectionCard>

                <SectionCard title="محله‌های مورد نظر" icon="📍" defaultOpen={false}>
                    {req.neighborhoods_data?.length ? (
                        <div className="prq-chips">
                            {req.neighborhoods_data.map((n, i) => (
                                <span key={i} className="prq-chip">{n.name}</span>
                            ))}
                        </div>
                    ) : <span className="prq-empty-inline">انتخاب نشده</span>}
                </SectionCard>

                <SectionCard title="خانوار" icon="👨‍👩‍👧" defaultOpen
                    canEdit={canEdit} editing={editing === 'household'}
                    onEditToggle={() => setEditing(e => e === 'household' ? null : 'household')}
                    editForm={<HouseholdEditForm req={req} onSave={d => patch('household', d)} onCancel={() => setEditing(null)} />}
                >
                    <DetailRow label="بزرگسال"    value={req.adults_count   != null ? `${req.adults_count} نفر`   : null} />
                    <DetailRow label="کودک"        value={req.children_count != null ? `${req.children_count} نفر` : null} />
                    <DetailRow label="حیوان خانگی" value={PET_LABELS[req.pet] || (req.pet && req.pet !== 'none' ? req.pet : 'ندارم')} />
                </SectionCard>

            </div>

            <div className="prq-matches-section">
                <h4 className="prq-matches-title">🏠 نتایج تطبیق</h4>
                <MatchResults requestId={req.id} />
            </div>
        </div>
    );
}

// ── list view ─────────────────────────────────────────────────────────────────

export default function PropertyRequestsPanel() {
    const navigate  = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState('');
    const [detail,   setDetail]   = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listRequests();
            setRequests(data?.results ?? data);
        } catch { setError('خطا در بارگذاری درخواست‌ها.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (id) => {
        if (!window.confirm('آیا از حذف این درخواست مطمئن هستید؟')) return;
        setDeleting(id);
        try {
            await deleteRequest(id);
            setRequests(prev => prev.filter(r => r.id !== id));
            if (detail?.id === id) setDetail(null);
        } catch { setError('خطا در حذف درخواست.'); }
        finally { setDeleting(null); }
    };

    if (detail) return <RequestDetail req={detail} onBack={() => setDetail(null)} />;

    return (
        <div className="prq-panel">
            <h2 className="prq-panel-title">درخواست‌های یافتن خانه</h2>
            <p className="prq-panel-desc">
                درخواست‌های ثبت‌شده شما. پس از فعال‌شدن سیستم تطبیق، نتایج هر درخواست اینجا نمایش داده می‌شود.
            </p>

            {error && <p className="prq-error">{error}</p>}

            {loading ? (
                <p className="prq-loading">در حال بارگذاری...</p>
            ) : requests.length === 0 ? (
                <div className="prq-empty">
                    <span className="prq-empty-icon">🏠</span>
                    <p>هنوز درخواستی ثبت نکرده‌اید.</p>
                    <button className="prq-btn prq-btn-view" onClick={() => navigate('/property-request')}>
                        + ثبت درخواست جدید
                    </button>
                </div>
            ) : (
                <div className="prq-list">
                    {requests.map(req => (
                        <div key={req.id} className="prq-card">
                            <div className="prq-card-top">
                                <div className="prq-card-info">
                                    <span className="prq-card-intent">{INTENT_LABELS[req.intent] || req.intent || 'درخواست'}</span>
                                    <StatusBadge status={req.status} />
                                </div>
                                <p className="prq-card-date">📅 {fmtDate(req.created_at)}</p>
                            </div>
                            <div className="prq-card-meta">
                                {req.property_type_name && <span>{req.property_type_name}</span>}
                                {req.usage_name         && <span>{req.usage_name}</span>}
                                {req.purchase_budget_max && <span>تا {fmt(req.purchase_budget_max)} تومان</span>}
                                {req.rent_budget_max     && <span>اجاره تا {fmt(req.rent_budget_max)} تومان</span>}
                                {(req.min_area_sqm || req.max_area_sqm) && (
                                    <span>{req.min_area_sqm || '—'}–{req.max_area_sqm || '—'} م²</span>
                                )}
                            </div>
                            <div className="prq-card-actions">
                                <button className="prq-btn prq-btn-view" onClick={() => setDetail(req)}>مشاهده</button>
                                <button className="prq-btn prq-btn-delete"
                                    onClick={() => handleDelete(req.id)} disabled={deleting === req.id}>
                                    {deleting === req.id ? '...' : 'حذف'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
